import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../config/queryKeys'
import {
  BalanceRoster,
  BalanceRostersPayload,
  getBalanceRosters,
} from '../../../services/teamBalance'

const EMPTY_PAYLOAD: BalanceRostersPayload = { rosters: [] }

interface UseBalanceRostersOptions {
  teamId?: string | null
  /**
   * The authenticated user id. Part of the cache key because the answer is
   * per-caller: two users on the same team legitimately have different rosters.
   * A stable primitive (not a token / mutable session handle), and the whole
   * query cache is cleared on login, so one user can never read another's entry.
   */
  authUserId?: string | number | null
  enabled?: boolean
}

/**
 * SN-4709 D40 (A-3) — "which rosters do I have balance on?".
 *
 * This is now the Balance tab-gating probe as well: the sheet itself is
 * roster-scoped, so the team-wide question the gate needs to answer is exactly
 * the one discovery answers. A 403 (non-manager) resolves silently to an empty
 * list — "no tab", never a toast — matching the previous probe's contract.
 */
export const useBalanceRosters = ({
  teamId,
  authUserId,
  enabled = true,
}: UseBalanceRostersOptions) => {
  const query = useQuery<BalanceRostersPayload>({
    queryKey: queryKeys.teamBalance.rosters(teamId, authUserId),
    queryFn: async () => {
      try {
        return await getBalanceRosters(teamId as string)
      } catch (error) {
        const status =
          (error as { status?: number; statusCode?: number })?.status ??
          (error as { status?: number; statusCode?: number })?.statusCode
        if (status === 403) {
          return EMPTY_PAYLOAD
        }
        throw error
      }
    },
    enabled: Boolean(teamId) && enabled,
    retry: false,
    staleTime: 30_000,
  })

  const rosters: BalanceRoster[] = query.data?.rosters ?? []

  return { ...query, rosters }
}

export default useBalanceRosters
